import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useTranslation } from '../context/LanguageContext';
import { joinPublicChallenge } from '../lib/api';
import { API_URL } from '../config/api';
import { Users, Trophy, Loader2, UserPlus } from 'lucide-react';

export default function JoinChallenge() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isDark } = useTheme();
  const { t } = useTranslation();
  const [challenge, setChallenge] = useState(null);
  const [loading, setLoading] = useState(true);
  const [joining, setJoining] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const loadChallenge = async () => {
      try {
        const sessionToken = localStorage.getItem('session_token');
        const headers = sessionToken ? { 'Authorization': `Bearer ${sessionToken}` } : {};
        const res = await fetch(`${API_URL}/api/challenges/${id}/public`, { headers });
        if (res.ok) {
          const data = await res.json();
          setChallenge(data);
        }
      } catch (err) {
        console.error('Error loading challenge:', err);
      } finally {
        setLoading(false);
      }
    };
    loadChallenge();
  }, [id]);

  const handleJoin = async () => {
    if (!user) {
      navigate(`/app/login?returnUrl=/join/challenge/${id}`);
      return;
    }
    setJoining(true);
    setError('');
    try {
      await joinPublicChallenge(id);
      navigate(`/app/challenge/${id}`);
    } catch (err) {
      setError(err?.response?.data?.detail || t('challenges.joinError') || 'Errore durante la partecipazione');
    } finally {
      setJoining(false);
    }
  };

  if (loading) {
    return (
      <div className={`h-screen flex items-center justify-center ${isDark ? 'bg-[#080D1A]' : 'bg-gray-50'}`}>
        <Loader2 className="w-8 h-8 animate-spin text-[#E8334A]" />
      </div>
    );
  }

  if (!challenge) {
    return (
      <div className={`h-screen flex flex-col items-center justify-center px-6 ${isDark ? 'bg-[#080D1A] text-white' : 'bg-gray-50 text-gray-900'}`}>
        <Trophy className="w-16 h-16 text-gray-400 mb-4" />
        <h2 className="text-xl font-bold mb-2">{t('challenges.notFound') || 'Sfida non trovata'}</h2>
        <p className={`text-sm mb-6 ${isDark ? 'text-white/50' : 'text-gray-500'}`}>
          {t('challenges.notFoundDesc') || 'Questa sfida potrebbe essere terminata o non esistere più.'}
        </p>
        <button
          onClick={() => navigate('/app/login')}
          className="px-6 py-3 rounded-full bg-[#E8334A] text-white font-medium"
        >
          {t('challenges.goToApp') || 'Vai all\'app'}
        </button>
      </div>
    );
  }

  const participantCount = challenge.participants_count || challenge.participants?.length || 0;

  return (
    <div className={`h-screen flex flex-col ${isDark ? 'bg-[#080D1A]' : 'bg-gray-50'}`}>
      <div className="flex-1 flex flex-col items-center justify-center px-6">
        {/* Challenge icon */}
        <div 
          className="w-24 h-24 rounded-3xl flex items-center justify-center text-5xl mb-6"
          style={{ backgroundColor: `${challenge.color || '#E8334A'}30` }}
        >
          {challenge.emoji || '🏆'}
        </div>

        {/* Challenge name */}
        <h1 className={`text-2xl font-bold text-center mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
          {challenge.name}
        </h1>

        {/* Participants */}
        <div className={`flex items-center gap-2 mb-6 ${isDark ? 'text-white/60' : 'text-gray-500'}`}>
          <Users className="w-4 h-4" />
          <span className="text-sm">
            {participantCount} {t('challenges.participants') || 'partecipanti'}
          </span>
        </div>

        {/* Description */}
        {challenge.description && (
          <p className={`text-center mb-8 max-w-sm ${isDark ? 'text-white/70' : 'text-gray-600'}`}>
            {challenge.description}
          </p>
        )}

        {error && (
          <div className="bg-red-500/20 text-red-300 p-3 rounded-xl text-sm text-center mb-4 w-full max-w-sm border border-red-500/30">
            {error}
          </div>
        )}

        {/* Join button */}
        <button
          onClick={handleJoin}
          disabled={joining}
          className="w-full max-w-sm py-4 rounded-2xl bg-[#E8334A] text-white font-semibold flex items-center justify-center gap-2 active:scale-[0.98] transition-transform"
          data-testid="join-challenge-btn"
        >
          {joining ? (
            <Loader2 className="w-5 h-5 animate-spin" />
          ) : (
            <>
              <UserPlus className="w-5 h-5" />
              {user ? (t('challenges.joinNow') || 'Partecipa ora') : (t('challenges.loginAndJoin') || 'Accedi e partecipa')}
            </>
          )}
        </button>

        {!user && (
          <p className={`text-xs mt-3 ${isDark ? 'text-white/40' : 'text-gray-400'}`}>
            {t('challenges.loginRequired') || 'Devi accedere per partecipare alla sfida'}
          </p>
        )}
      </div>
    </div>
  );
}
